import { forwardRef, Ref, FC } from 'react'

import { clsx } from 'clsx'

import { useBackgroundContext } from '../BackgroundContext/index.js'

export type CheckboxProps = Omit<React.ComponentProps<'input'>, 'size'> & {
  invalid?: boolean
  placement?: 'start' | 'end' | 'middle'
  noLabel?: boolean
  size?: 'sm' | 'md'
}
/**
 * The Checkbox component is used in forms when a user needs to select multiple values from several options.
 */
export const Checkbox: FC<CheckboxProps> = forwardRef(
  (props, ref: Ref<HTMLInputElement>) => {
    const {
      invalid = false,
      disabled,
      placement = 'start',
      children,
      className,
      noLabel = false,
      size = 'sm',
      ...rest
    } = props
    const { text500Clsx } = useBackgroundContext()

    const input = (
      <input
        ref={ref}
        type="checkbox"
        className={clsx(
          size === 'sm' && 'w-4 h-4',
          size === 'md' && 'w-5 h-5',
          'rounded',
          'text-action-primary',
          'focus:outline-none focus:ring-transparent',
          !invalid && 'focus-visible:ring-focused border-line',
          invalid &&
            'border-line-negative border-2 focus-visible:ring-line-negative focus:border-line-negative',
          disabled && 'opacity-60 cursor-default',
          className,
        )}
        disabled={disabled}
        {...rest}
      />
    )

    if (noLabel) {
      return input
    }

    if (placement === 'start') {
      return (
        <label className="relative flex items-start">
          <div className="flex items-center h-5">{input}</div>
          <div
            className={clsx(
              'ms-3 text-sm',
              text500Clsx,
              disabled && 'opacity-60 cursor-default',
            )}
          >
            {children}
          </div>
        </label>
      )
    }
    if (placement === 'middle') {
      return (
        <label className="relative flex items-center">
          <div className="flex items-center h-5">{input}</div>
          <div
            className={clsx(
              'ms-3 text-sm',
              text500Clsx,
              disabled && 'opacity-60 cursor-default',
            )}
          >
            {children}
          </div>
        </label>
      )
    }
    return (
      <label className="relative flex items-start">
        <div
          className={clsx(
            'min-w-0 flex-1 text-sm',
            text500Clsx,
            disabled && 'opacity-60 cursor-default',
          )}
        >
          {children}
        </div>
        <div className="ms-3 flex items-center h-5">{input}</div>
      </label>
    )
  },
)
